import CheckIcon from '@mui/icons-material/Check';
import Tooltip from '@mui/material/Tooltip';
import { TFunction } from 'i18next';
import { TableColumn, TableFilterOption } from '@components/Table';
import { MfaStatus } from '@shared/constants/mfa';
import { getEventDayAndTime } from '@utils/timeHelpers';
import { AlertType, UsersTableOptions, UsersTableRow } from './types';
import { getInitials, getStatusLabel, parseUsersTableDate } from './helpers';
import QuickActions from './QuickActions';
import {
  AttentionFlag,
  DateCell,
  MentorAvatar,
  MentorCell,
  MentorOverflow,
  NameCell,
  PhoneExists,
  SecondaryText,
  StatusPill,
  UserAvatar,
  UserCell,
} from './styles';

const getAlertLabel = (alertType: AlertType, t: TFunction) => {
  switch (alertType) {
    case AlertType.NEW_USER_NO_COURSE:
      return t('UsersTable.alerts.newUserNoCourse');
    case AlertType.NEW_USER_NO_THERAPIST:
      return t('UsersTable.alerts.newUserNoTherapist');
    case AlertType.USER_INACTIVE:
      return t('UsersTable.alerts.userInactive');
    default:
      return '';
  }
};

const toFilterOptions = (items: { id: string; name: string }[]): TableFilterOption[] => (
  items.map(({ id, name }) => ({ value: id, label: name }))
);

const joinNames = (items: { name: string }[]) => items.map(({ name }) => name).join(', ') || '-';

type GetColumnsOptions = {
  filterOptions: UsersTableOptions;
  showBusinessUnits: boolean;
  loadOptions: () => void;
  isTherapist: boolean;
  pendingVideoUserId: string | null;
  isStartingVideo: boolean;
  onStartVideoChat: (user: UsersTableRow) => void;
  onOpenRecords: (user: UsersTableRow) => void;
  t: TFunction;
};

export const getColumns = ({
  filterOptions,
  showBusinessUnits,
  loadOptions,
  isTherapist,
  pendingVideoUserId,
  isStartingVideo,
  onStartVideoChat,
  onOpenRecords,
  t,
}: GetColumnsOptions
): TableColumn<UsersTableRow>[] => [
  {
    key: 'alerts',
    title: '!',
    width: 66,
    align: 'right',
    filter: {
      type: 'multi-select',
      options: Object.values(AlertType).map((alertType) => ({
        value: alertType,
        label: getAlertLabel(alertType, t),
      })),
    },
    filterField: 'alertTypes',
    render: ({ alerts }) => {
      if (!alerts.length) return '';
      return (
        <Tooltip
          title={alerts.map(({ id, alertType }) => (
            <div key={id}>{getAlertLabel(alertType, t)}</div>
          ))}
          placement="bottom"
          arrow
        >
          <AttentionFlag aria-label={t('UsersTable.attentionNeeded')}>!</AttentionFlag>
        </Tooltip>
      );
    },
  },
  {
    key: 'user',
    title: 'User',
    width: showBusinessUnits ? '17%' : '20%',
    sortable: true,
    sortField: 'NAME',
    filter: { type: 'text', placeholder: 'Search by name...' },
    filterField: 'name',
    render: ({ firstName, lastName, email, age }) => {
      const fullName = `${firstName} ${lastName}`.trim();
      const details = [age == null ? null : `${age} years`, email].filter(Boolean).join(' | ');
      return (
        <Tooltip title={<>{fullName}<br />{details}</>} placement="bottom" arrow>
          <UserCell>
            <UserAvatar>{getInitials(fullName)}</UserAvatar>
            <NameCell>
              <span>{fullName}</span>
              <SecondaryText>{age == null ? '-' : `${age} years`}</SecondaryText>
            </NameCell>
          </UserCell>
        </Tooltip>
      );
    },
  },
  ...(showBusinessUnits ? [{
    key: 'businessUnits',
    title: 'Business Unit',
    width: '12%',
    filter: {
      type: 'multi-select' as const,
      options: toFilterOptions(filterOptions.businessUnits),
      searchable: true,
      onOpen: loadOptions,
    },
    filterField: 'businessUnitIds',
    render: (row: UsersTableRow) => joinNames(row.businessUnits),
  }] : []),
  {
    key: 'accountStatus',
    title: 'Status',
    width: '12%',
    filter: {
      type: 'multi-select',
      options: [
        { value: 'ACTIVE', label: 'Active' },
        { value: 'INACTIVE', label: 'Inactive' },
        { value: 'INVITE_PENDING', label: 'Invite Pending' },
      ],
    },
    filterField: 'accountStatuses',
    render: ({ accountStatus }) => accountStatus ? (
      <StatusPill
        tone={accountStatus === 'ACTIVE'
          ? 'success'
          : accountStatus === 'INACTIVE' ? 'error' : 'neutral'}
      >
        {getStatusLabel(accountStatus)}
      </StatusPill>
    ) : '-',
  },
  {
    key: 'mfaStatus',
    title: 'MFA Status',
    width: '8%',
    render: ({ mfaStatus }) => mfaStatus ? (
      <StatusPill tone={mfaStatus === MfaStatus.INACTIVE ? 'neutral' : 'success'}>
        {getStatusLabel(mfaStatus)}
      </StatusPill>
    ) : '-',
  },
  {
    key: 'phone',
    title: 'Phone Exists',
    width: '7%',
    align: 'center',
    render: ({ phone }) => phone ? (
      <Tooltip title={phone} placement="bottom" arrow>
        <PhoneExists><CheckIcon fontSize="inherit" /></PhoneExists>
      </Tooltip>
    ) : '-',
  },
  {
    key: 'mentors',
    title: 'Mentor',
    width: '13%',
    filter: {
      type: 'multi-select',
      options: toFilterOptions(filterOptions.mentors),
      searchable: true,
      onOpen: loadOptions,
    },
    filterField: 'mentorIds',
    render: ({ mentors }) => {
      if (!mentors.length) return '-';
      return (
        <Tooltip
          title={mentors.map(({ id, name }) => <div key={id}>{name}</div>)}
          placement="bottom"
          arrow
        >
          <MentorCell>
            <MentorAvatar>{getInitials(mentors[0].name)}</MentorAvatar>
            {mentors.length > 1 && <MentorOverflow>{`+${mentors.length - 1}`}</MentorOverflow>}
          </MentorCell>
        </Tooltip>
      );
    },
  },
  {
    key: 'course',
    title: 'Course',
    width: '12%',
    render: ({ course }) => course?.name || '-',
  },
  {
    key: 'skill',
    title: 'Skill',
    width: '11%',
    render: ({ skill }) => skill?.name || '-',
  },
  {
    key: 'lastPracticedAt',
    title: 'Last Practiced',
    width: '12%',
    sortable: true,
    sortField: 'LAST_PRACTICED',
    render: ({ lastPracticedAt }) => {
      const parsedDate = parseUsersTableDate(lastPracticedAt);
      if (!parsedDate) return '-';
      const date = getEventDayAndTime(parsedDate);
      return <DateCell>{date.day}<small>{date.time}</small></DateCell>;
    },
  },
  {
    key: 'quickActions',
    title: t('UsersTable.quickActions.title'),
    width: '10%',
    align: 'center',
    render: (user: UsersTableRow) => (
      <QuickActions
        user={user}
        isTherapist={isTherapist}
        isVideoStarting={pendingVideoUserId === user.id}
        videoDisabled={isStartingVideo}
        onStartVideoChat={onStartVideoChat}
        onOpenRecords={onOpenRecords}
      />
    ),
  },
];